import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { secureStorage } from '@/services/storage/secureStorage.service';
import { STORAGE_KEYS } from '@/constants/routes.const';
import type { ApiError } from '@/types/api.types';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'https://api.example.com/v1';
const MAX_RETRIES = 2;
const AUTH_ENDPOINTS = ['/auth/login', '/auth/register', '/auth/refresh'];

export const http: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
});

/** Keeps API service methods consistent by returning only the typed response payload. */
export function responseData<T>(response: AxiosResponse<T>): T {
  return response.data;
}

// ---- Request interceptor: attach auth header ----
http.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const token = await secureStorage.get(STORAGE_KEYS.ACCESS_TOKEN);
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  // Set Content-Type for JSON requests only if not FormData and not already set
  if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData) && !config.headers['Content-Type']) {
    config.headers['Content-Type'] = 'application/json';
  }
  return config;
});

// ---- Response interceptor: refresh token + retry logic ----
let isRefreshing = false;
let pendingQueue: Array<(token: string | null) => void> = [];

function flushQueue(token: string | null) {
  pendingQueue.forEach((cb) => cb(token));
  pendingQueue = [];
}

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retryCount?: number;
      _isRetryForAuth?: boolean;
    };

    // Network / timeout retry with exponential backoff
    const isNetworkError = !error.response;
    if (isNetworkError && originalRequest) {
      originalRequest._retryCount = originalRequest._retryCount ?? 0;
      if (originalRequest._retryCount < MAX_RETRIES) {
        originalRequest._retryCount += 1;
        const delay = 400 * 2 ** originalRequest._retryCount;
        await new Promise((resolve) => setTimeout(resolve, delay));
        return http(originalRequest);
      }
    }

    // 401 handling: attempt a single silent token refresh
    const requestPath = originalRequest?.url
      ? new URL(originalRequest.url, BASE_URL).pathname.replace(/\/+$/, '')
      : '';
    const isPublicAuthRequest = AUTH_ENDPOINTS.some((path) => requestPath.endsWith(path));
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._isRetryForAuth &&
      !isPublicAuthRequest
    ) {
      originalRequest._isRetryForAuth = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push((token) => {
            if (!token) return reject(error);
            originalRequest.headers.set('Authorization', `Bearer ${token}`);
            resolve(http(originalRequest));
          });
        });
      }

      isRefreshing = true;
      try {
        const refreshToken = await secureStorage.get(STORAGE_KEYS.REFRESH_TOKEN);
        if (!refreshToken) throw error;

        const { data } = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken });
        await secureStorage.set(STORAGE_KEYS.ACCESS_TOKEN, data.accessToken);
        await secureStorage.set(STORAGE_KEYS.REFRESH_TOKEN, data.refreshToken);

        flushQueue(data.accessToken);
        originalRequest.headers.set('Authorization', `Bearer ${data.accessToken}`);
        return http(originalRequest);
      } catch (refreshError) {
        flushQueue(null);
        await secureStorage.remove(STORAGE_KEYS.ACCESS_TOKEN);
        await secureStorage.remove(STORAGE_KEYS.REFRESH_TOKEN);
        await secureStorage.remove(STORAGE_KEYS.USER);
        await secureStorage.remove(STORAGE_KEYS.LAST_ACTIVITY);
        // Let the auth store listen for this event and redirect to /login
        window.dispatchEvent(new CustomEvent('auth:expired'));
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    const normalized: ApiError = {
      status: error.response?.status ?? 0,
      message:
        (error.response?.data as { message?: string })?.message ??
        error.message ??
        'Unexpected network error',
      fieldErrors: (error.response?.data as { fieldErrors?: Record<string, string> })?.fieldErrors,
      retryAfterSeconds: parseRetryAfter(error.response?.headers?.['retry-after']),
    };
    return Promise.reject(normalized);
  },
);

function parseRetryAfter(value: string | number | undefined): number | undefined {
  if (value === undefined || value === '') return undefined;
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds > 0) return Math.ceil(seconds);
  const date = Date.parse(String(value));
  if (!Number.isNaN(date)) {
    const delta = Math.ceil((date - Date.now()) / 1000);
    return delta > 0 ? delta : undefined;
  }
  return undefined;
}
